import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { keepAliveService } from '../services/keepAlive';

interface StartupLoadingState {
  isLoading: boolean;
  isFirstLoad: boolean;
  estimatedTime: number;
}

interface StartupLoadingContextType extends StartupLoadingState {
  completeStartup: () => Promise<any>;
  triggerStartupLoading: (estimatedTime?: number) => void;
  hideStartupLoading: () => void;
  checkAndWarmupBackend: () => Promise<boolean>;
}

const StartupLoadingContext = createContext<StartupLoadingContextType | undefined>(undefined);

export const useStartupLoading = () => {
  const context = useContext(StartupLoadingContext);
  if (context === undefined) {
    throw new Error('useStartupLoading must be used within a StartupLoadingProvider');
  }
  return context;
};

interface StartupLoadingProviderProps {
  children: ReactNode;
}

export const StartupLoadingProvider: React.FC<StartupLoadingProviderProps> = ({ children }) => {
  const [state, setState] = useState<StartupLoadingState>({
    isLoading: false,
    isFirstLoad: true,
    estimatedTime: 20
  });

  // Don't automatically show loading screen on mount
  // Instead, only show it when we actually detect a cold backend
  useEffect(() => {
    const lastActivity = localStorage.getItem('last_backend_activity');
    const isFirstTime = !lastActivity;
    
    setState(prev => ({
      ...prev,
      isFirstLoad: isFirstTime,
      isLoading: false // Don't auto-show loading
    }));
  }, []);

  // Check if backend is cold and trigger warmup if needed
  const checkAndWarmupBackend = useCallback(async (): Promise<boolean> => {
    try {
      // First, try a quick health check
      const healthResponse = await fetch('/api/v1/health', {
        method: 'GET',
        timeout: 3000 // 3 second timeout
      } as RequestInit);

      if (healthResponse.ok) {
        // Backend is already warm
        localStorage.setItem('last_backend_activity', Date.now().toString());
        return true;
      }
    } catch (error) {
      // Backend might be cold, show loading and warm it up
      // console.log('[StartupLoading] Backend appears cold, warming up...');
      
      setState(prev => ({
        ...prev,
        isLoading: true
      }));

      try {
        const warmupResult = await keepAliveService.warmup();
        localStorage.setItem('last_backend_activity', Date.now().toString());
        
        setState(prev => ({
          ...prev,
          isLoading: false
        }));

        return warmupResult ? true : false;
      } catch (warmupError) {
        console.error('Backend warmup failed:', warmupError);
        setState(prev => ({
          ...prev,
          isLoading: false
        }));
        return false;
      }
    }
    return false;
  }, []);

  // Warmup and hide loading screen
  const completeStartup = useCallback(async () => {
    try {
      // Try to warmup the backend
      const warmupResult = await keepAliveService.warmup();
      
      // Store current time as last activity
      localStorage.setItem('last_backend_activity', Date.now().toString());
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        isFirstLoad: false
      }));

      return warmupResult;
    } catch (error) {
      console.error('Startup warmup failed:', error);
      // Still hide loading screen even if warmup fails
      setState(prev => ({
        ...prev,
        isLoading: false,
        isFirstLoad: false
      }));
      return null;
    }
  }, []);

  // Manually trigger startup loading (for testing or forced refresh)
  const triggerStartupLoading = useCallback((estimatedTime: number = 20) => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      estimatedTime
    }));
  }, []);

  // Hide startup loading immediately
  const hideStartupLoading = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: false
    }));
  }, []);

  const contextValue: StartupLoadingContextType = {
    ...state,
    completeStartup,
    triggerStartupLoading,
    hideStartupLoading,
    checkAndWarmupBackend
  };

  return (
    <StartupLoadingContext.Provider value={contextValue}>
      {children}
    </StartupLoadingContext.Provider>
  );
}; 